/**
 * تجميعات للمبيانات وتصدير CSV — تقرير التمارين اليومية (صف الإدارة).
 */

export function aggregateDailyExercisesReportCharts(rows, formatLevelName) {
  const levelMap = new Map();
  const deptMap = new Map();
  const buckets = { '0-25': 0, '25-50': 0, '50-75': 0, '75-100': 0, '?': 0 };

  for (const raw of rows) {
    const totalSessions = raw.total_sessions ?? raw.totalSessions;
    const avgPct = raw.avg_session_pct ?? raw.avgSessionPct;
    const r = { ...raw, total_sessions: totalSessions, avg_session_pct: avgPct };

    const lvl = r.soroban_level_name
      ? formatLevelName(r.soroban_level_name)
      : r.soroban_level_number != null
        ? String(r.soroban_level_number)
        : null;
    const levelKey = lvl || '—';
    levelMap.set(levelKey, (levelMap.get(levelKey) || 0) + 1);

    const dept = (r.department_name && String(r.department_name).trim()) || '—';
    const prev = deptMap.get(dept) || { students: 0, totalSessions: 0 };
    prev.students += 1;
    prev.totalSessions += Number(r.total_sessions) || 0;
    deptMap.set(dept, prev);

    const p = Number(r.avg_session_pct);
    if (Number.isNaN(p) || r.avg_session_pct == null || r.avg_session_pct === '') {
      buckets['?'] += 1;
    } else if (p < 25) buckets['0-25'] += 1;
    else if (p < 50) buckets['25-50'] += 1;
    else if (p < 75) buckets['50-75'] += 1;
    else buckets['75-100'] += 1;
  }

  const byLevel = [...levelMap.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const byDept = [...deptMap.entries()]
    .map(([name, v]) => ({ name, students: v.students, totalSessions: v.totalSessions }))
    .sort((a, b) => b.totalSessions - a.totalSessions)
    .slice(0, 12);

  const bucketOrder = ['0-25', '25-50', '50-75', '75-100', '?'];
  const byBucket = bucketOrder.map((key) => ({ key, count: buckets[key] }));

  return { byLevel, byDept, byBucket };
}

function csvEscapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const BUCKET_I18N = {
  '0-25': 'chartBucket_0_25',
  '25-50': 'chartBucket_25_50',
  '50-75': 'chartBucket_50_75',
  '75-100': 'chartBucket_75_100',
  '?': 'chartBucket_unknown'
};

/**
 * يُلحق بملف CSV بعد جدول التفاصيل (نفس صفوف التجميع المعروضة في المبيانات).
 */
export function buildDailyExercisesChartCsvAppend(rows, formatLevelName, t) {
  if (!rows || rows.length === 0) return '';
  const { byLevel, byDept, byBucket } = aggregateDailyExercisesReportCharts(rows, formatLevelName);
  const lines = ['', csvEscapeCell(t('reports.dailyExercisesApi.csvChartBlockTitle')), ''];

  lines.push(csvEscapeCell(t('reports.dailyExercisesApi.chartByLevelTitle')));
  lines.push(
    [t('reports.dailyExercisesApi.colLevel'), t('reports.dailyExercisesApi.chartStudentCount')]
      .map(csvEscapeCell)
      .join(',')
  );
  for (const row of byLevel) {
    lines.push([row.name, row.count].map(csvEscapeCell).join(','));
  }

  lines.push('');
  lines.push(csvEscapeCell(t('reports.dailyExercisesApi.chartByDepartmentTitle')));
  lines.push(
    [
      t('reports.dailyExercisesApi.colDepartment'),
      t('reports.dailyExercisesApi.chartStudentCount'),
      t('reports.dailyExercisesApi.chartTotalSessions')
    ]
      .map(csvEscapeCell)
      .join(',')
  );
  for (const row of byDept) {
    lines.push([row.name, row.students, row.totalSessions].map(csvEscapeCell).join(','));
  }

  lines.push('');
  lines.push(csvEscapeCell(t('reports.dailyExercisesApi.chartAvgBucketsTitle')));
  lines.push(
    [t('reports.dailyExercisesApi.chartRangeLabel'), t('reports.dailyExercisesApi.chartStudentCount')]
      .map(csvEscapeCell)
      .join(',')
  );
  for (const row of byBucket) {
    const labelKey = `reports.dailyExercisesApi.${BUCKET_I18N[row.key] || 'chartBucket_unknown'}`;
    lines.push([t(labelKey), row.count].map(csvEscapeCell).join(','));
  }

  return `\r\n${lines.join('\r\n')}`;
}
